// Core
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

// Libraries
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

// Plugins
import { formatCrusherModel } from '../../plugins/crusher';
import {
  CRUSHER_MODEL_IMAGE_MAP,
  timestampToDateStrText,
  timestampToTimeAgo,
} from '../../plugins/general';
import { RequestMethod, requestVantageApi } from '../../plugins/request';

// Store
import { getCustomer, getUser } from '../../store/session.store';

// Types
import { CrusherModel, EquipmentUnitCrusher } from '../../types/crusher';
import {
  Customer,
  EdgeDeviceOperationalStatusStatus,
  EquipmentUnitInstallStatus,
  User,
  UserType,
} from '../../types/global';

// Components
import {
  CardList,
  CardRow,
} from '../../layout/elements/CardList';
import { CustomerPanel } from '../../layout/CustomerPanel';
import { Loader, LoaderSize } from '../../layout/elements/Loader';
import { StatusDot, StatusDotStatus } from '../../components/StatusDot';
import { Table, TableColumnProps } from '../../layout/elements/Table';

// Component
import {
  CardsContainer,
  Container,
  EquipmentImageContainer,
  InstalledAtContainer,
  LoadingContainer,
  SuperiorNumberRowContainer,
  TableContainer,
} from './PlantNavigation.style';

// Breakpoint for switching between table and cards
const TABLE_BREAKPOINT: number = 1050;

// Polling interval for equipment status updates (1 minute)
// @todo: Convert to WebSocket for real-time updates in the future
const POLLING_INTERVAL_MS: number = 60000;

function PlantNavigation(): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();

  // Check if screen is large enough for table view
  const isTableView: boolean = useMediaQuery({ minWidth: TABLE_BREAKPOINT });

  /* ***********************************************************************************************
  ***************************************** LOCAL STATES *******************************************
  *********************************************************************************************** */

  interface PlantNavigationState {
    equipmentUnits: EquipmentUnitCrusher[],
    equipmentModels: CrusherModel[],
    isLoading: boolean,
  }

  const initialState: PlantNavigationState = {
    equipmentUnits: [],
    equipmentModels: [],
    isLoading: false,
  };

  const [state, setState] = useState(initialState);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  // Get customer
  const customer: Customer | null = getCustomer();

  // Get user
  const user: User | null = getUser();

  // Fetch equipment data
  const fetchEquipmentData = useCallback(async (showLoading: boolean = false): Promise<void> => {
    if (showLoading) {
      setState((prevState: PlantNavigationState) => ({ ...prevState, isLoading: true }));
    }

    try {
      const equipmentUnitsResponse: EquipmentUnitCrusher[] = await requestVantageApi.equipmentUnit({
        method: RequestMethod.GET,
        path: '/crusher',
        params: user?.type === UserType.admin || user?.type === UserType.service
          ? { idCustomer: customer?.id }
          : {},
      });

      setState((prevState: PlantNavigationState) => ({
        ...prevState,
        equipmentUnits: equipmentUnitsResponse,
        isLoading: false,
      }));
    } catch (error: unknown) {
      // Clear data on error to avoid displaying stale information
      setState((prevState: PlantNavigationState) => ({
        ...prevState,
        equipmentUnits: [],
        isLoading: false,
      }));
    }
  }, [customer, user?.type]);

  // Get status text helper
  const getStatusText = (installStatus: string): string => {
    switch (installStatus) {
      case EquipmentUnitInstallStatus.pre:
        return t('equipment.unit.installStatus.preRegistered');
      case EquipmentUnitInstallStatus.installed:
        return t('equipment.unit.installStatus.installed');
      case EquipmentUnitInstallStatus.removed:
        return t('equipment.unit.installStatus.removed');
      default:
        return installStatus;
    }
  };

  // Get status dot status from equipment unit data
  const getStatusDotStatus = (
    equipmentUnit: EquipmentUnitCrusher,
  ): StatusDotStatus => {
    if (equipmentUnit.installStatus === EquipmentUnitInstallStatus.pre) {
      return StatusDotStatus.offline;
    }
    return equipmentUnit.edgeStatus === EdgeDeviceOperationalStatusStatus.on
      ? StatusDotStatus.online
      : StatusDotStatus.offline;
  };

  // Get "Last Update" text for equipment unit
  const getLastUpdateText = (equipmentUnit: EquipmentUnitCrusher): string => {
    // Non-installed equipment: empty field (same treatment as "Installed at")
    if (equipmentUnit.installStatus === EquipmentUnitInstallStatus.pre) {
      return '';
    }

    // Installed equipment that never communicated: "Never seen" / "Nunca visto"
    if (equipmentUnit.lastHeartbeatTimestamp === null) {
      return t('view.plantNavigation.neverSeen');
    }

    // Equipment with heartbeat: "time ago" format
    return timestampToTimeAgo(equipmentUnit.lastHeartbeatTimestamp);
  };

  // Columns for Table
  const tableColumns: TableColumnProps[] = [
    {
      key: 'serialNumber',
      dataIndex: 'serialNumber',
      title: `${t('common.serialNumber')}`,
      sorter: true,
      render: (text: string, unitData: EquipmentUnitCrusher): JSX.Element => (
        <SuperiorNumberRowContainer>
          <StatusDot
            testId={`status-dot-${unitData.id}`}
            status={getStatusDotStatus(unitData)}
          />
          <img
            src={CRUSHER_MODEL_IMAGE_MAP[unitData.model]}
            alt={formatCrusherModel(unitData.model)}
          />
          {text}
        </SuperiorNumberRowContainer>
      ),
    },
    {
      key: 'model',
      dataIndex: 'model',
      title: `${t('common.superiorPartNumber')}`,
      sorter: true,
      render: (_text: string, unitData: EquipmentUnitCrusher): string => (
        formatCrusherModel(unitData.model)
      ),
    },
    {
      key: 'customerUnitName',
      dataIndex: 'customerUnitName',
      title: `${t('common.customerUnitName')}`,
      sorter: (a, b) => (
        a.customerUnitName || formatCrusherModel(a.model)
      ).localeCompare(b.customerUnitName || formatCrusherModel(b.model)),
      render: (customerUnitName: string, record: EquipmentUnitCrusher): string => (
        customerUnitName || formatCrusherModel(record.model)
      ),
    },
    {
      key: 'status',
      dataIndex: 'installStatus',
      title: `${t('common.status')}`,
      sorter: true,
      render: (installStatus: string) => getStatusText(installStatus),
    },
    {
      key: 'installedAt',
      dataIndex: 'installedAt',
      title: `${t('common.installedAt')}`,
      sorter: (a, b) => (a.installedAt || 0) - (b.installedAt || 0),
      render: (_text: string, equipmentUnit): JSX.Element | string => (
        <InstalledAtContainer>
          {equipmentUnit.installedAt ? timestampToDateStrText(equipmentUnit.installedAt) : ''}
        </InstalledAtContainer>
      ),
    },
    {
      key: 'lastHeartbeatTimestamp',
      dataIndex: 'lastHeartbeatTimestamp',
      title: `${t('common.lastUpdateEdgeDevice')}`,
      sorter: (a, b) => (a.lastHeartbeatTimestamp || 0) - (b.lastHeartbeatTimestamp || 0),
      render: (_text: string, equipmentUnit: EquipmentUnitCrusher): string => (
        getLastUpdateText(equipmentUnit)
      ),
    },
  ];

  // Generate equipment data rows for Table (keeping original model for image mapping)
  const tableDataRows: EquipmentUnitCrusher[] = state.equipmentUnits.map(
    (equipmentUnit: EquipmentUnitCrusher) => ({
      ...equipmentUnit,
      key: equipmentUnit.id,
    }),
  );

  // Generate card rows for CardList
  const cardRows: CardRow[] = state.equipmentUnits.map(
    (equipmentUnit: EquipmentUnitCrusher): CardRow => ({
      key: equipmentUnit.id,
      title: equipmentUnit.customerUnitName || formatCrusherModel(equipmentUnit.model),
      subtitle: equipmentUnit.serialNumber,
      image: (
        <EquipmentImageContainer>
          <StatusDot
            testId={`status-dot-mobile-${equipmentUnit.id}`}
            status={getStatusDotStatus(equipmentUnit)}
          />
          <img
            src={CRUSHER_MODEL_IMAGE_MAP[equipmentUnit.model]}
            alt={formatCrusherModel(equipmentUnit.model)}
          />
        </EquipmentImageContainer>
      ),
      disabled: equipmentUnit.installStatus !== EquipmentUnitInstallStatus.installed,
      subItems: [
        {
          label: `${t('common.superiorPartNumber')}`,
          value: formatCrusherModel(equipmentUnit.model),
        },
        {
          label: `${t('common.status')}`,
          value: getStatusText(equipmentUnit.installStatus),
        },
        {
          label: `${t('common.installedAt')}`,
          value: equipmentUnit.installedAt
            ? timestampToDateStrText(equipmentUnit.installedAt)
            : '',
        },
        {
          label: `${t('common.lastUpdateEdgeDevice')}`,
          value: getLastUpdateText(equipmentUnit),
        },
      ],
      action: {
        label: `${t('common.goToDashboard')}`,
        onClick: () => navigate(`/crusher/${equipmentUnit.id}`),
      },
    }),
  );

  // Render table view
  const renderTableView = (): JSX.Element => (
    <TableContainer>
      <Table
        columns={tableColumns}
        rows={tableDataRows}
        fixedHeader
        onClickRow={(equipmentUnit: EquipmentUnitCrusher) => ({
          'data-testid': `equipment-table-row-${equipmentUnit.id}`,
          style: {
            cursor: equipmentUnit.installStatus === EquipmentUnitInstallStatus.pre
              ? 'not-allowed'
              : 'pointer',
          },
          onClick: () => {
            if (equipmentUnit.installStatus !== EquipmentUnitInstallStatus.pre) {
              navigate(`/crusher/${equipmentUnit.id}`);
            }
          },
        })}
      />
    </TableContainer>
  );

  // Render cards view
  const renderCardsView = (): JSX.Element => (
    <CardsContainer>
      <CardList
        testId="plant-navigation-cards"
        rows={cardRows}
        sortColumn={{ key: 'subtitle', label: `${t('common.serialNumber')}` }}
      />
    </CardsContainer>
  );

  /* ***********************************************************************************************
  ************************************ COMPONENT HANDLING ******************************************
  *********************************************************************************************** */

  useEffect((): (() => void) => {
    // Fetch data immediately on mount (with loading)
    fetchEquipmentData(true);

    // Set up polling interval (without loading)
    const intervalId: NodeJS.Timeout = setInterval((): void => {
      fetchEquipmentData(false);
    }, POLLING_INTERVAL_MS);

    // Cleanup: stop polling when component unmounts
    return (): void => {
      clearInterval(intervalId);
    };
  }, [fetchEquipmentData]);

  return (
    <CustomerPanel>
      <Container>
        {state.isLoading && (
          <LoadingContainer>
            <Loader size={LoaderSize.xl} text={`${t('common.loaderInfo')}`} />
          </LoadingContainer>
        )}

        {!state.isLoading && (isTableView ? renderTableView() : renderCardsView())}
      </Container>
    </CustomerPanel>
  );
}

export { PlantNavigation };
